'use client'
import createOptimisticStateProvider from '@/components/context-factories/create-optimistic-state-provider'
import withSuspendedContext from '@/components/context-factories/with-suspended-context'
import wishlist from '@/lib/api/shopware/wishlist'
import { PropsWithChildren } from 'react'

type WishlistData = Record<string, boolean>

type WishlistAction =
  | {
      type: 'ADD'
      payload: string
    }
  | {
      type: 'REMOVE'
      payload: string
    }

const reducer = (state: WishlistData, action: WishlistAction) => {
  switch (action.type) {
    case 'ADD':
      return {
        ...state,
        [action.payload]: true,
      }

    case 'REMOVE':
      return {
        ...state,
        [action.payload]: false,
      }

    default:
      return state
  }
}

type WishlistInitialState = Awaited<ReturnType<
  typeof wishlist.getProducts
> | null>

type WishlistProviderProps = PropsWithChildren<{
  value: WishlistInitialState
}>

const [Provider, useProvider] = createOptimisticStateProvider(reducer)

const [WishlistProvider, useWishlistProvider, withWishListSuspense] =
  withSuspendedContext(
    ({ value, children }: WishlistProviderProps) => (
      <Provider
        initialState={(value ?? []).reduce(
          (acc, item) => {
            acc[item.id] = true
            return acc
          },
          {} as Record<string, boolean>,
        )}
      >
        {children}
      </Provider>
    ),
    useProvider,
  )

const useWishlistDispatch = () => {
  const { dispatch } = useWishlistProvider()
  return dispatch
}

const useIsFavorite = (productId: string) => {
  const { state } = useWishlistProvider()
  return state[productId] ?? false
}

const useWishlistTotal = () => {
  const { state } = useWishlistProvider()
  return Object.values(state).filter(Boolean).length
}

export {
  WishlistProvider,
  useWishlistTotal,
  useWishlistDispatch,
  useIsFavorite,
  withWishListSuspense,
}
